import { useEffect, useState } from 'react';
import { Button, Input, Select, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal } from '../../../shared/components';
import { PlusIcon, TrashIcon, EditIcon } from '../../../shared/icons';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';
import { Employee, TemporalCompany, Position } from '../../../shared/services/humanResources/types';

const repo = getHumanResourcesRepository();

const emptyForm = { documentNumber: '', fullName: '', temporalCompany: '', position: '', monthlySalary: '' };
type FormState = typeof emptyForm;

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
}

/**
 * Formatea un salario para mostrarlo en el input mientras se escribe, con
 * puntos como separador de miles (formato colombiano: "1.423.500").
 * IMPORTANTE: el valor que se guarda en el estado del formulario (ver
 * `handleSalaryChange`) es SIEMPRE solo dígitos, sin puntos - los puntos son
 * puramente de presentación. Este es el fix del bug donde un
 * `<input type="number">` interpretaba el "." que digitaba el usuario como
 * separador decimal (formato en-US) y truncaba "1.423.500" a "1".
 */
function formatSalaryDigits(digits: string): string {
  if (!digits) return '';
  return new Intl.NumberFormat('es-CO').format(Number(digits));
}

function employeeToForm(e: Employee): FormState {
  return {
    documentNumber: e.documentNumber,
    fullName: e.fullName,
    temporalCompany: e.temporalCompany ?? '',
    position: e.position ?? '',
    monthlySalary: String(e.monthlySalary),
  };
}

/**
 * Pestaña "Empleados": roster de colaboradores a liquidar (equivalente a los
 * datos maestros de la hoja RESUMEN del Excel - Cedula, Nombre, Temporal,
 * Cargo, Salario). NO son usuarios de LCC, no inician sesion.
 *
 * Incluye edición completa de un empleado ya guardado (el Excel permitía
 * corregir cualquier celda de estos datos maestros directamente; antes aquí
 * solo se podía activar/desactivar o eliminar).
 */
export function EmployeesTab() {
  const toast = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [temporalCompanies, setTemporalCompanies] = useState<TemporalCompany[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleting, setDeleting] = useState<Employee | null>(null);

  const [editing, setEditing] = useState<Employee | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const load = () => {
    Promise.all([repo.listEmployees(true), repo.listTemporalCompanies(), repo.listPositions()]).then(([e, tc, p]) => {
      setEmployees(e);
      setTemporalCompanies(tc);
      setPositions(p);
      setIsLoading(false);
    });
  };
  useEffect(() => { load(); }, []);

  const temporalOptions = temporalCompanies.map((c) => ({ value: c.name, label: c.name }));
  const positionOptions = positions.map((p) => ({ value: p.name, label: p.name }));

  const handleSalaryChange = (raw: string, setter: (f: (prev: FormState) => FormState) => void) => {
    const digits = raw.replace(/\D/g, '');
    setter((f) => ({ ...f, monthlySalary: digits }));
  };

  const addEmployee = async () => {
    if (!form.documentNumber.trim() || !form.fullName.trim() || !form.monthlySalary) {
      toast.show('Cédula, nombre y salario son obligatorios.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      await repo.createEmployee({
        documentNumber: form.documentNumber.trim(),
        fullName: form.fullName.trim(),
        temporalCompany: form.temporalCompany || null,
        position: form.position || null,
        monthlySalary: Number(form.monthlySalary),
      });
      setForm(emptyForm);
      toast.show('Empleado agregado.');
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo agregar el empleado.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const openEdit = (e: Employee) => {
    setEditing(e);
    setEditForm(employeeToForm(e));
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.documentNumber.trim() || !editForm.fullName.trim() || !editForm.monthlySalary) {
      toast.show('Cédula, nombre y salario son obligatorios.', 'error');
      return;
    }
    setIsSavingEdit(true);
    try {
      await repo.updateEmployee(editing.id, {
        documentNumber: editForm.documentNumber.trim(),
        fullName: editForm.fullName.trim(),
        temporalCompany: editForm.temporalCompany || null,
        position: editForm.position || null,
        monthlySalary: Number(editForm.monthlySalary),
      });
      toast.show('Empleado actualizado.');
      setEditing(null);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo actualizar el empleado.', 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const toggleActive = async (e: Employee) => {
    await repo.updateEmployee(e.id, { isActive: !e.isActive });
    load();
  };

  const columns: Column<Employee>[] = [
    { key: 'documentNumber', header: 'Cédula', render: (e) => e.documentNumber },
    { key: 'fullName', header: 'Nombre', render: (e) => e.fullName, sortable: true, sortValue: (e) => e.fullName },
    { key: 'temporalCompany', header: 'Temporal', render: (e) => e.temporalCompany ?? '—' },
    { key: 'position', header: 'Cargo', render: (e) => e.position ?? '—' },
    { key: 'salary', header: 'Salario', render: (e) => formatCurrency(e.monthlySalary) },
    {
      key: 'status',
      header: 'Estado',
      render: (e) => (
        <button onClick={() => toggleActive(e)}>
          <Badge tone={e.isActive ? 'success' : 'neutral'}>{e.isActive ? 'Activo' : 'Inactivo'}</Badge>
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (e) => (
        <div className="flex items-center gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(e)} className="h-9 w-9 text-ink-500 hover:bg-ink-50" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(e)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Input placeholder="Cédula" value={form.documentNumber} onChange={(e) => setForm((f) => ({ ...f, documentNumber: e.target.value }))} />
        <Input placeholder="Nombre completo" value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} />
        <Select placeholder="Temporal (opcional)" options={temporalOptions} value={form.temporalCompany} onChange={(e) => setForm((f) => ({ ...f, temporalCompany: e.target.value }))} />
        <Select placeholder="Cargo (opcional)" options={positionOptions} value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))} />
        <Input
          placeholder="Salario mensual"
          inputMode="numeric"
          value={formatSalaryDigits(form.monthlySalary)}
          onChange={(e) => handleSalaryChange(e.target.value, setForm)}
        />
      </div>
      <Button size="md" leftIcon={<PlusIcon width={18} height={18} />} onClick={addEmployee} isLoading={isSaving}>
        Agregar empleado
      </Button>
      <DataTable columns={columns} rows={employees} rowKey={(e) => e.id} isLoading={isLoading} emptyTitle="Sin empleados registrados" />
      <ConfirmDialog
        open={!!deleting}
        title="Eliminar empleado"
        description={`¿Eliminar a ${deleting?.fullName}? Se recomienda desactivarlo en vez de eliminarlo si tiene registros históricos de horas.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteEmployee(deleting.id);
          setDeleting(null);
          load();
        }}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing ? `Editar empleado · ${editing.fullName}` : ''}>
        {editing && (
          <div className="space-y-3">
            <Input label="Cédula" value={editForm.documentNumber} onChange={(e) => setEditForm((f) => ({ ...f, documentNumber: e.target.value }))} />
            <Input label="Nombre completo" value={editForm.fullName} onChange={(e) => setEditForm((f) => ({ ...f, fullName: e.target.value }))} />
            <Select label="Temporal" placeholder="(ninguna)" options={temporalOptions} value={editForm.temporalCompany} onChange={(e) => setEditForm((f) => ({ ...f, temporalCompany: e.target.value }))} />
            <Select label="Cargo" placeholder="(ninguno)" options={positionOptions} value={editForm.position} onChange={(e) => setEditForm((f) => ({ ...f, position: e.target.value }))} />
            <Input
              label="Salario mensual"
              inputMode="numeric"
              value={formatSalaryDigits(editForm.monthlySalary)}
              onChange={(e) => handleSalaryChange(e.target.value, setEditForm)}
            />
            <Button size="md" fullWidth onClick={saveEdit} isLoading={isSavingEdit}>
              Guardar cambios
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
